// ./src/components/features/VehicleFinancing/VehicleFinancingList/VehicleFinancingList.tsx
//
// List component for vehicle financing records.
//
// Context support (matches LogbookList pattern):
// - When rendered inside a <VehicleFinancingProvider>, data props
//   (financingRecords, loading) are read from context automatically.
//   Props can still override context values if provided.
// - When rendered outside a provider, all data must be passed via props.
//
// Design decisions:
// - Unified render path: useMediaQuery for mobile detection, single card component
// - Swipeable cards on mobile, inline action buttons on desktop
// - Specialized card content (ring progress, cost breakdown, mileage tracking)
// - Uses shared useEntranceAnimation hook + entranceListVariants/entranceCardVariants

import React, { useMemo, useCallback, useState, useEffect } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Skeleton,
  Divider,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";

import { FinancingCostBreakdown } from "@/utils";
import {
  VehicleFinancing,
  VEHICLE_FINANCING_STATUS,
  FINANCING_TYPE,
  formatFinancingAmount,
  formatInterestRate,
  SCHEDULE_TYPE,
} from "@/utils/api";
import { useAppEnvironment, useUserProfileContext } from "@/contexts";
import { useOptionalVehicleFinancingContext } from "@/contexts";
import {
  AppButton,
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

// ============================================
// Props
// ============================================

export interface VehicleFinancingListProps {
  /**
   * Array of vehicle financing records to display.
   * Optional when inside a VehicleFinancingProvider — reads from context.
   */
  financingRecords?: VehicleFinancing[];

  /**
   * Loading state for initial load.
   * Optional when inside a VehicleFinancingProvider — reads from context.
   */
  loading?: boolean;

  /** Loading state for loading more items. Optional. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional. */
  hasMore?: boolean;

  /** Callback to load more items. Optional. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEditFinancing?: (financing: VehicleFinancing) => void;

  /** Callback when delete action is clicked */
  onDeleteFinancing?: (financing: VehicleFinancing) => void;

  /** ID of financing currently being deleted. Optional. */
  deletingFinancingId?: string | null;

  /** ID of recently edited/added financing to highlight */
  highlightedFinancingId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAddFinancing?: () => void;
}

// ============================================
// Constants
// ============================================

const PACE_CONFIG: Record<
  string,
  { color: string; icon: string; labelKey: string }
> = {
  under_pace: {
    color: "teal",
    icon: "TrendingDown",
    labelKey: "vehicleFinancing:list.mileage.underPace",
  },
  on_pace: {
    color: "blue",
    icon: "Minus",
    labelKey: "vehicleFinancing:list.mileage.onPace",
  },
  over_pace: {
    color: "orange",
    icon: "TrendingUp",
    labelKey: "vehicleFinancing:list.mileage.overPace",
  },
};

const STATUS_CONFIG: Record<number, { color: string }> = {
  [VEHICLE_FINANCING_STATUS.ACTIVE]: { color: "teal" },
  [VEHICLE_FINANCING_STATUS.COMPLETED]: { color: "gray" },
};

const TYPE_CONFIG: Record<string, { color: string; icon: string }> = {
  [FINANCING_TYPE.LOAN]: { color: "blue", icon: "BuildingBank" },
  [FINANCING_TYPE.LEASE]: { color: "violet", icon: "Key" },
};

// ============================================
// Helper Functions
// ============================================

const formatDateRange = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  t: (key: string) => string
): string | null => {
  if (!startDate && !endDate) return null;
  const start = startDate ? dayjs(startDate).format("MMM DD, YYYY") : null;
  const end = endDate ? dayjs(endDate).format("MMM DD, YYYY") : null;
  if (start && end) return `${start} — ${end}`;
  if (start)
    return t("vehicleFinancing:list.startingFrom").replace("{{date}}", start);
  if (end) return t("vehicleFinancing:list.until").replace("{{date}}", end);
  return null;
};

const getProgressColor = (percent: number | null | undefined): string => {
  if (percent == null) return "blue";
  if (percent >= 90) return "teal";
  if (percent >= 50) return "blue";
  return "cyan";
};

const getPaymentFrequencySuffix = (
  scheduleType: string | null | undefined,
  t: (key: string) => string
): string => {
  switch (scheduleType) {
    case SCHEDULE_TYPE.WEEKLY:
      return t("vehicleFinancing:list.perWeek");
    case SCHEDULE_TYPE.YEARLY:
      return t("vehicleFinancing:list.perYear");
    case SCHEDULE_TYPE.ONE_TIME:
      return t("vehicleFinancing:list.oneTime");
    case SCHEDULE_TYPE.MONTHLY:
    default:
      return t("vehicleFinancing:list.perMonth");
  }
};

/**
 * Build a FinancingCostBreakdown display object from backend-computed fields.
 */
const buildCostBreakdownFromApi = (
  financing: VehicleFinancing
): FinancingCostBreakdown | null => {
  if (financing.costTotal == null || financing.costTotal <= 0) return null;
  const isLease = financing.financingType === FINANCING_TYPE.LEASE;

  return {
    isLease,
    principal: financing.costPrincipal ?? 0,
    totalInterest: financing.costTotalInterest ?? 0,
    totalCost: financing.costTotal,
    interestPercent: financing.costInterestPercent ?? 0,
    downPaymentBarPercent: financing.costDownPaymentBarPercent ?? 0,
    principalBarPercent: financing.costPrincipalBarPercent ?? 0,
    interestBarPercent: financing.costInterestBarPercent ?? 0,
    downPayment: financing.downPayment ?? 0,
    residualValue: isLease ? financing.residualValue : undefined,
  };
};

// ============================================
// Animated Ring Progress (SVG + framer-motion)
// ============================================

interface AnimatedRingProps {
  value: number;
  size?: number;
  thickness?: number;
  color: string;
  animate?: boolean;
}

const AnimatedRing: React.FC<AnimatedRingProps> = ({
  value,
  size = 80,
  thickness = 6,
  color,
  animate = true,
}) => {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, value));

  return (
    <Box
      style={{
        width: size,
        height: size,
        position: "relative",
        flexShrink: 0,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--mantine-color-gray-2)"
          strokeWidth={thickness}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`var(--mantine-color-${color}-5)`}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={animate ? { strokeDashoffset: circumference } : undefined}
          animate={{
            strokeDashoffset:
              circumference - (clamped / 100) * circumference,
          }}
          transition={{
            duration: animate ? 0.9 : 0,
            ease: [0.34, 1.56, 0.64, 1],
            delay: animate ? 0.3 : 0,
          }}
        />
      </svg>
      <Box
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <motion.div
          initial={animate ? { opacity: 0, scale: 0.6 } : undefined}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.4,
            delay: animate ? 0.6 : 0,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        >
          <Text size="lg" fw={700} c={`${color}.6`} lh={1} ta="center">
            {clamped.toFixed(0)}%
          </Text>
        </motion.div>
      </Box>
    </Box>
  );
};

// ============================================
// Animated Progress Bar
// ============================================

const AnimatedProgressBar: React.FC<{
  value: number;
  color: string;
}> = ({ value, color }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      style={{
        height: 6,
        borderRadius: 3,
        backgroundColor: "var(--mantine-color-gray-2)",
        overflow: "hidden",
      }}
    >
      <motion.div
        initial={{ width: "0%" }}
        animate={{
          width: mounted
            ? `${Math.min(100, Math.max(0, value))}%`
            : "0%",
        }}
        transition={{
          duration: 0.8,
          ease: [0.34, 1.56, 0.64, 1],
          delay: 0.2,
        }}
        style={{
          height: "100%",
          borderRadius: 3,
          backgroundColor: `var(--mantine-color-${color}-5)`,
        }}
      />
    </Box>
  );
};

// ============================================
// Cost Breakdown Visual Component
// ============================================

interface CostBreakdownBarProps {
  breakdown: FinancingCostBreakdown;
  currency: string;
  typeColor: string;
}

const AnimatedCostBreakdownBar: React.FC<CostBreakdownBarProps> = ({
  breakdown,
  currency,
  typeColor,
}) => {
  const { t } = useTranslation(["vehicleFinancing"]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 150);
    return () => clearTimeout(timer);
  }, []);

  const principalLabel = breakdown.isLease
    ? t("vehicleFinancing:list.depreciation")
    : t("vehicleFinancing:list.principal");
  const interestLabel = breakdown.isLease
    ? t("vehicleFinancing:list.financeCharges")
    : t("vehicleFinancing:list.totalInterest");
  const totalLabel = breakdown.isLease
    ? t("vehicleFinancing:list.totalLeaseCost")
    : t("vehicleFinancing:list.totalCost");

  return (
    <Stack gap={8}>
      <Group justify="space-between" align="center">
        <Text size="sm" c="dimmed" fw={500}>
          {totalLabel}
        </Text>
        <Text size="sm" fw={700}>
          {formatFinancingAmount(breakdown.totalCost, currency)}
        </Text>
      </Group>

      <Tooltip.Group>
        <Box
          style={{
            height: 10,
            borderRadius: 5,
            backgroundColor: "var(--mantine-color-gray-2)",
            overflow: "hidden",
            display: "flex",
          }}
        >
          {breakdown.downPayment > 0 && (
            <Tooltip
              label={`${t("vehicleFinancing:list.downPaymentLabel")}: ${formatFinancingAmount(breakdown.downPayment, currency)}`}
              position="top"
              withArrow
            >
              <motion.div
                initial={{ width: "0%" }}
                animate={{
                  width: mounted
                    ? `${breakdown.downPaymentBarPercent}%`
                    : "0%",
                }}
                transition={{
                  duration: 0.8,
                  ease: [0.34, 1.56, 0.64, 1],
                  delay: 0.2,
                }}
                style={{
                  height: "100%",
                  backgroundColor: "var(--mantine-color-teal-4)",
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
          <Tooltip
            label={`${principalLabel}: ${formatFinancingAmount(breakdown.principal, currency)}`}
            position="top"
            withArrow
          >
            <motion.div
              initial={{ width: "0%" }}
              animate={{
                width: mounted
                  ? `${breakdown.principalBarPercent}%`
                  : "0%",
              }}
              transition={{
                duration: 0.8,
                ease: [0.34, 1.56, 0.64, 1],
                delay: breakdown.downPayment > 0 ? 0.3 : 0.2,
              }}
              style={{
                height: "100%",
                backgroundColor: `var(--mantine-color-${typeColor}-5)`,
                flexShrink: 0,
              }}
            />
          </Tooltip>
          {breakdown.totalInterest > 0 && (
            <Tooltip
              label={`${interestLabel}: ${formatFinancingAmount(breakdown.totalInterest, currency)}`}
              position="top"
              withArrow
            >
              <motion.div
                initial={{ width: "0%" }}
                animate={{
                  width: mounted
                    ? `${breakdown.interestBarPercent}%`
                    : "0%",
                }}
                transition={{
                  duration: 0.8,
                  ease: [0.34, 1.56, 0.64, 1],
                  delay: breakdown.downPayment > 0 ? 0.4 : 0.35,
                }}
                style={{
                  height: "100%",
                  backgroundColor: "var(--mantine-color-orange-4)",
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Tooltip.Group>

      <Group gap="lg" wrap="wrap">
        <Group gap={6} wrap="nowrap">
          <Box
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: `var(--mantine-color-${typeColor}-5)`,
              flexShrink: 0,
            }}
          />
          <Text size="xs" c="dimmed">
            {principalLabel}:{" "}
            <Text span size="xs" fw={600} c="dark">
              {formatFinancingAmount(breakdown.principal, currency)}
            </Text>
          </Text>
        </Group>
        {breakdown.totalInterest > 0 && (
          <Group gap={6} wrap="nowrap">
            <Box
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                backgroundColor: "var(--mantine-color-orange-4)",
                flexShrink: 0,
              }}
            />
            <Text size="xs" c="dimmed">
              {interestLabel}:{" "}
              <Text span size="xs" fw={600} c="dark">
                {formatFinancingAmount(breakdown.totalInterest, currency)}
              </Text>
              {breakdown.interestPercent > 0 && (
                <Text span size="xs" c="dimmed">
                  {" "}
                  ({breakdown.interestPercent}%)
                </Text>
              )}
            </Text>
          </Group>
        )}
        {breakdown.downPayment > 0 && (
          <Group gap={6} wrap="nowrap">
            <Box
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                backgroundColor: "var(--mantine-color-teal-4)",
                flexShrink: 0,
              }}
            />
            <Text size="xs" c="dimmed">
              {t("vehicleFinancing:list.downPaymentLabel")}:{" "}
              <Text span size="xs" fw={600} c="dark">
                {formatFinancingAmount(breakdown.downPayment, currency)}
              </Text>
            </Text>
          </Group>
        )}
        {breakdown.residualValue != null &&
          breakdown.residualValue > 0 && (
            <Group gap={6} wrap="nowrap">
              <Box
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  backgroundColor: "var(--mantine-color-gray-4)",
                  flexShrink: 0,
                }}
              />
              <Text size="xs" c="dimmed">
                {t("vehicleFinancing:list.residualValue")}:{" "}
                <Text span size="xs" fw={600} c="dark">
                  {formatFinancingAmount(
                    breakdown.residualValue,
                    currency
                  )}
                </Text>
              </Text>
            </Group>
          )}
      </Group>
    </Stack>
  );
};

const CostBreakdownSection = React.memo(AnimatedCostBreakdownBar);

// ============================================
// Mileage Tracking Section (lease only)
// ============================================

interface MileageTrackingProps {
  tracking: {
    unit: string;
    totalAllowance: number;
    driven: number | null | undefined;
    usedPercent: number | null | undefined;
    annualRate: number | null | undefined;
    projectedTotal: number | null | undefined;
    projectedOverage: number | null | undefined;
    projectedOverageCost: number | null | undefined;
    paceStatus: string | null | undefined;
    dataPoints: number | null | undefined;
  };
  currency: string;
}

const MileageTrackingSectionComponent: React.FC<MileageTrackingProps> = ({
  tracking,
  currency,
}) => {
  const { t } = useTranslation(["vehicleFinancing"]);
  const pace = tracking.paceStatus
    ? PACE_CONFIG[tracking.paceStatus]
    : null;
  const usedPercent = tracking.usedPercent ?? 0;
  const barColor = pace
    ? pace.color
    : usedPercent > 100
      ? "red"
      : "blue";

  return (
    <Stack gap={8}>
      <Group justify="space-between" align="center">
        <Group gap={6} wrap="nowrap">
          <Text size="sm" c="dimmed" fw={500}>
            {t("vehicleFinancing:list.mileage.title")}
          </Text>
          {pace && (
            <Group gap={4} wrap="nowrap">
              <AppIcon
                iconName={pace.icon}
                size={14}
                color={`var(--mantine-color-${pace.color}-6)`}
              />
              <Text size="xs" fw={600} c={pace.color}>
                {t(pace.labelKey)}
              </Text>
            </Group>
          )}
        </Group>
        {tracking.driven != null && (
          <Text size="sm" fw={600}>
            {Math.round(tracking.driven).toLocaleString()} /{" "}
            {tracking.totalAllowance.toLocaleString()} {tracking.unit}
          </Text>
        )}
      </Group>

      {tracking.driven != null && (
        <AnimatedProgressBar
          value={Math.min(usedPercent, 100)}
          color={barColor}
        />
      )}

      <Group gap="lg" wrap="wrap">
        {tracking.annualRate != null && (
          <Text size="xs" c="dimmed">
            {t("vehicleFinancing:list.mileage.annualRate")}:{" "}
            <Text span size="xs" fw={600} c="dark">
              {Math.round(tracking.annualRate).toLocaleString()}{" "}
              {tracking.unit}/
              {t("vehicleFinancing:list.mileage.yr")}
            </Text>
          </Text>
        )}
        {tracking.projectedTotal != null && (
          <Text size="xs" c="dimmed">
            {t("vehicleFinancing:list.mileage.projectedTotal")}:{" "}
            <Text span size="xs" fw={600} c="dark">
              {Math.round(
                tracking.projectedTotal
              ).toLocaleString()}{" "}
              {tracking.unit}
            </Text>
          </Text>
        )}
        {tracking.projectedOverage != null &&
          tracking.projectedOverage > 0 && (
            <Text size="xs" c="dimmed">
              {t("vehicleFinancing:list.mileage.projectedOverage")}:{" "}
              <Text span size="xs" fw={600} c="orange">
                {Math.round(
                  tracking.projectedOverage
                ).toLocaleString()}{" "}
                {tracking.unit}
              </Text>
              {tracking.projectedOverageCost != null &&
                tracking.projectedOverageCost > 0 && (
                  <Text span size="xs" c="red" fw={600}>
                    {" "}
                    (
                    {formatFinancingAmount(
                      tracking.projectedOverageCost,
                      currency
                    )}
                    )
                  </Text>
                )}
            </Text>
          )}
        {tracking.dataPoints != null &&
          tracking.dataPoints > 0 &&
          tracking.dataPoints < 5 && (
            <Tooltip
              label={t(
                "vehicleFinancing:list.mileage.lowDataTooltip"
              )}
              withArrow
            >
              <Group
                gap={4}
                wrap="nowrap"
                style={{ cursor: "help" }}
              >
                <AppIcon
                  iconName="InfoCircle"
                  size={12}
                  color="var(--mantine-color-gray-5)"
                />
                <Text size="xs" c="gray.5">
                  {t(
                    "vehicleFinancing:list.mileage.dataPoints",
                    { count: tracking.dataPoints }
                  )}
                </Text>
              </Group>
            </Tooltip>
          )}
      </Group>
    </Stack>
  );
};

const MileageTrackingSection = React.memo(MileageTrackingSectionComponent);

// ============================================
// Sub-Components
// ============================================

const FinancingTypeBadge: React.FC<{ financingType?: string }> = ({
  financingType,
}) => {
  const { t } = useTranslation(["vehicleFinancing"]);
  if (!financingType) return null;

  const config =
    TYPE_CONFIG[financingType] || TYPE_CONFIG[FINANCING_TYPE.LOAN];
  const label =
    financingType === FINANCING_TYPE.LOAN
      ? t("vehicleFinancing:financingType.loan")
      : t("vehicleFinancing:financingType.lease");

  return (
    <Group gap={6} wrap="nowrap">
      <ThemeIcon variant="light" color={config.color} size="sm" radius="xl">
        <AppIcon iconName={config.icon} size={14} />
      </ThemeIcon>
      <Text size="sm" fw={600} c={config.color} tt="uppercase" lts={0.5}>
        {label}
      </Text>
    </Group>
  );
};

const FinancingStatusIndicator: React.FC<{ status?: number }> = ({
  status,
}) => {
  const { t } = useTranslation(["vehicleFinancing"]);
  if (status == null) return null;

  const config =
    STATUS_CONFIG[status] ||
    STATUS_CONFIG[VEHICLE_FINANCING_STATUS.ACTIVE];
  let label: string;
  switch (status) {
    case VEHICLE_FINANCING_STATUS.ACTIVE:
      label = t("vehicleFinancing:status.active");
      break;
    case VEHICLE_FINANCING_STATUS.COMPLETED:
      label = t("vehicleFinancing:status.completed");
      break;
    default:
      label = t("vehicleFinancing:status.unknown");
  }

  return (
    <Group gap={6} wrap="nowrap">
      <Box
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: `var(--mantine-color-${config.color}-5)`,
          boxShadow:
            status === VEHICLE_FINANCING_STATUS.ACTIVE
              ? `0 0 6px var(--mantine-color-${config.color}-3)`
              : undefined,
        }}
      />
      <Text size="sm" c="dimmed" fw={500}>
        {label}
      </Text>
    </Group>
  );
};

const DetailItem: React.FC<{
  icon: string;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <Group gap={6} wrap="nowrap">
    <AppIcon
      iconName={icon}
      size={16}
      color="gray"
      style={{ flexShrink: 0 }}
    />
    <Text size="sm" c="dimmed" lineClamp={1}>
      {children}
    </Text>
  </Group>
);

// ============================================
// Loading Skeletons
// ============================================

const FinancingCardSkeleton: React.FC = () => (
  <Paper p="lg" withBorder radius="md">
    <Group wrap="nowrap" align="flex-start" gap="xl">
      <Stack gap="md" style={{ flex: 1 }}>
        <Group justify="space-between" align="flex-start">
          <Stack gap={6}>
            <Skeleton height={22} width={180} />
            <Skeleton height={16} width={140} />
          </Stack>
        </Group>
        <Skeleton height={1} width="100%" />
        <Group gap="xl">
          <Stack gap={4}>
            <Skeleton height={14} width={55} />
            <Skeleton height={18} width={85} />
          </Stack>
          <Stack gap={4}>
            <Skeleton height={14} width={70} />
            <Skeleton height={18} width={55} />
          </Stack>
          <Stack gap={4}>
            <Skeleton height={14} width={35} />
            <Skeleton height={18} width={100} />
          </Stack>
        </Group>
        <Skeleton height={10} width="100%" radius="xl" />
        <Group gap="lg">
          <Skeleton height={12} width={100} />
          <Skeleton height={12} width={120} />
        </Group>
      </Stack>
      <Box visibleFrom="sm">
        <Skeleton height={80} width={80} circle />
      </Box>
    </Group>
  </Paper>
);

const LoadingSkeleton: React.FC = () => (
  <Stack gap="md">
    {Array.from({ length: 3 }).map((_, i) => (
      <FinancingCardSkeleton key={i} />
    ))}
  </Stack>
);

// ============================================
// Financing Card Component
// ============================================

interface FinancingCardProps {
  financing: VehicleFinancing;
  onEdit?: (financing: VehicleFinancing) => void;
  onDelete?: (financing: VehicleFinancing) => void;
  highlighted: boolean;
  isDeleting: boolean;
  isMobile: boolean;
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const FinancingCardComponent: React.FC<FinancingCardProps> = ({
  financing,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  isMobile,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["vehicleFinancing", "common"]);
  const { userProfile } = useUserProfileContext();
  const currency =
    financing.financingCurrency ||
    userProfile?.homeCurrency ||
    "USD";
  const isCompleted =
    financing.status === VEHICLE_FINANCING_STATUS.COMPLETED;
  const isLease = financing.financingType === FINANCING_TYPE.LEASE;
  const typeConfig =
    TYPE_CONFIG[financing.financingType || ""] ||
    TYPE_CONFIG[FINANCING_TYPE.LOAN];

  // Payment info
  const paymentDisplay = useMemo(() => {
    const schedule = financing.expenseSchedule;
    if (!schedule?.totalPrice) return null;
    const payCurrency = schedule.paidInCurrency || currency;
    return {
      amount: formatFinancingAmount(schedule.totalPrice, payCurrency),
      suffix: getPaymentFrequencySuffix(schedule.scheduleType, t),
    };
  }, [financing.expenseSchedule, currency, t]);

  // Next payment date
  const nextPaymentDisplay = useMemo(() => {
    if (!financing.expenseSchedule?.nextScheduledAt) return null;
    return dayjs(financing.expenseSchedule.nextScheduledAt).format(
      "MMM DD, YYYY"
    );
  }, [financing.expenseSchedule?.nextScheduledAt]);

  // Date range
  const dateRangeDisplay = useMemo(
    () => formatDateRange(financing.startDate, financing.endDate, t),
    [financing.startDate, financing.endDate, t]
  );

  // Mileage tracking (leases with odometer data)
  const mileageTracking = useMemo(() => {
    if (!isLease) return null;
    const unit = financing.mileageAllowanceUnit || "km";
    const totalAllowance = financing.totalMileageAllowance;
    if (!totalAllowance) return null;

    return {
      unit,
      totalAllowance,
      driven: financing.mileageDriven,
      usedPercent: financing.mileageUsedPercent,
      annualRate: financing.annualMileageRate,
      projectedTotal: financing.projectedTotalMileage,
      projectedOverage: financing.projectedOverage,
      projectedOverageCost: financing.projectedOverageCost,
      paceStatus: financing.mileagePaceStatus,
      dataPoints: financing.mileageDataPoints,
    };
  }, [isLease, financing]);

  // Cost breakdown from backend-computed fields
  const costBreakdown = useMemo(
    () => buildCostBreakdownFromApi(financing),
    [financing]
  );

  // Remaining balance for leases
  const leaseRemainingDisplay = useMemo(() => {
    if (!isLease) return null;
    if (financing.remainingBalance != null) {
      return formatFinancingAmount(financing.remainingBalance, currency);
    }
    const schedule = financing.expenseSchedule;
    if (
      financing.paymentsRemaining != null &&
      financing.paymentsRemaining > 0 &&
      schedule?.totalPrice &&
      schedule.totalPrice > 0
    ) {
      const remaining =
        financing.paymentsRemaining * schedule.totalPrice;
      return formatFinancingAmount(
        remaining,
        schedule.paidInCurrency || currency
      );
    }
    return null;
  }, [isLease, financing, currency]);

  // Progress
  const percent = financing.percentComplete ?? 0;
  const progressColor = getProgressColor(financing.percentComplete);
  const hasProgress = financing.percentComplete != null;

  // Swipe actions (mobile)
  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(financing),
        disabled: isDeleting,
      });
    }
    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(financing),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }
    return actions;
  }, [onEdit, onDelete, financing, isDeleting, t]);

  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => onSwipeStateChange?.(isOpen),
    [onSwipeStateChange]
  );

  // Bottom detail items
  const detailItems = useMemo(() => {
    const items: { icon: string; text: string }[] = [];

    if (dateRangeDisplay) {
      items.push({ icon: "CalendarWeek", text: dateRangeDisplay });
    }

    if (
      financing.endDate &&
      financing.status === VEHICLE_FINANCING_STATUS.ACTIVE
    ) {
      items.push({
        icon: "Flag",
        text: t("vehicleFinancing:list.paidOffBy").replace(
          "{{date}}",
          dayjs(financing.endDate).format("MMM YYYY")
        ),
      });
    }

    if (mileageTracking && !mileageTracking.driven) {
      items.push({
        icon: "Gauge",
        text: `${mileageTracking.totalAllowance.toLocaleString()} ${mileageTracking.unit} ${t("vehicleFinancing:list.totalAllowance")}`,
      });
    }

    return items;
  }, [financing, dateRangeDisplay, mileageTracking, t]);

  // ── Card content ──
  const cardContent = (
    <Paper
      p="lg"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 200ms ease",
        borderLeft: `3px solid var(--mantine-color-${typeConfig.color}-${isCompleted ? "2" : "5"})`,
        ...(highlighted
          ? {
            borderColor: "var(--mantine-color-teal-5)",
            boxShadow:
              "0 0 0 1px var(--mantine-color-teal-2), 0 4px 16px var(--mantine-color-teal-1)",
          }
          : {}),
      }}
    >
      <Group wrap="nowrap" align="stretch" gap="xl">
        {/* ── Left: all info ── */}
        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <Stack gap={4}>
            <Text size="xl" fw={700} lineClamp={1}>
              {financing.car?.label ||
                t("vehicleFinancing:list.unknownVehicle")}
            </Text>
            <Group gap={8} wrap="nowrap">
              {financing.lenderName && (
                <Text size="md" c="dimmed" lineClamp={1}>
                  {financing.lenderName}
                </Text>
              )}
              {financing.agreementNumber && (
                <Text size="sm" c="dimmed" fs="italic">
                  #{financing.agreementNumber}
                </Text>
              )}
            </Group>
          </Stack>

          {/* Type + Status */}
          <Group gap="md" wrap="nowrap">
            <FinancingTypeBadge
              financingType={financing.financingType}
            />
            <FinancingStatusIndicator status={financing.status} />
          </Group>

          <Divider />

          {/* Key stats */}
          <Group gap="xl" wrap="wrap">
            {paymentDisplay && (
              <Stack gap={2}>
                <Text size="sm" c="dimmed" fw={500}>
                  {t("vehicleFinancing:list.columns.payment")}
                </Text>
                <Group gap={4} wrap="nowrap" align="baseline">
                  <Text size="lg" fw={700}>
                    {paymentDisplay.amount}
                  </Text>
                  <Text size="sm" c="dimmed" fw={500}>
                    {paymentDisplay.suffix}
                  </Text>
                </Group>
              </Stack>
            )}
            {!paymentDisplay &&
              financing.totalAmount != null &&
              financing.totalAmount > 0 && (
                <Stack gap={2}>
                  <Text size="sm" c="dimmed" fw={500}>
                    {isLease
                      ? t("vehicleFinancing:financingType.lease")
                      : t("vehicleFinancing:financingType.loan")}
                  </Text>
                  <Text size="lg" fw={700}>
                    {formatFinancingAmount(
                      financing.totalAmount,
                      currency
                    )}
                  </Text>
                </Stack>
              )}

            {financing.interestRate != null &&
              financing.interestRate > 0 && (
                <Stack gap={2}>
                  <Text size="sm" c="dimmed" fw={500}>
                    {t(
                      "vehicleFinancing:form.fields.interestRate.label"
                    )}
                  </Text>
                  <Text size="lg" fw={700}>
                    {formatInterestRate(financing.interestRate)}
                  </Text>
                </Stack>
              )}

            {financing.paymentsRemaining != null &&
              financing.paymentsRemaining > 0 ? (
              <Stack gap={2}>
                <Text size="sm" c="dimmed" fw={500}>
                  {t("vehicleFinancing:list.columns.term")}
                </Text>
                <Text size="lg" fw={700}>
                  {t(
                    "vehicleFinancing:list.paymentsRemaining"
                  ).replace(
                    "{{count}}",
                    String(financing.paymentsRemaining)
                  )}
                </Text>
              </Stack>
            ) : financing.termMonths ? (
              <Stack gap={2}>
                <Text size="sm" c="dimmed" fw={500}>
                  {t("vehicleFinancing:list.columns.term")}
                </Text>
                <Text size="lg" fw={700}>
                  {financing.termMonths}{" "}
                  {t("vehicleFinancing:list.months")}
                </Text>
              </Stack>
            ) : null}

            {financing.financingType === FINANCING_TYPE.LOAN &&
              financing.remainingBalance != null && (
                <Stack gap={2}>
                  <Text
                    size="sm"
                    c="dimmed"
                    fw={500}
                    style={{ textTransform: "capitalize" }}
                  >
                    {t("vehicleFinancing:list.remainingBalance")
                      .replace("{{amount}}", "")
                      .trim()}
                  </Text>
                  <Text size="lg" fw={700}>
                    {formatFinancingAmount(
                      financing.remainingBalance,
                      currency
                    )}
                  </Text>
                </Stack>
              )}

            {isLease && leaseRemainingDisplay && (
              <Stack gap={2}>
                <Text size="sm" c="dimmed" fw={500}>
                  {t("vehicleFinancing:list.columns.remaining")}
                </Text>
                <Text size="lg" fw={700}>
                  {leaseRemainingDisplay}
                </Text>
              </Stack>
            )}

            {nextPaymentDisplay && (
              <Stack gap={2}>
                <Text size="sm" c="dimmed" fw={500}>
                  {t("vehicleFinancing:list.next")}
                </Text>
                <Text size="lg" fw={700}>
                  {nextPaymentDisplay}
                </Text>
              </Stack>
            )}
          </Group>

          {/* Cost Breakdown */}
          {costBreakdown && (
            <CostBreakdownSection
              breakdown={costBreakdown}
              currency={currency}
              typeColor={typeConfig.color}
            />
          )}

          {/* Mileage Tracking (lease only) */}
          {mileageTracking && mileageTracking.driven != null && (
            <MileageTrackingSection
              tracking={mileageTracking}
              currency={currency}
            />
          )}

          {/* Mobile: animated progress bar */}
          {hasProgress && isMobile && (
            <Stack gap={4}>
              <Group justify="space-between" align="center">
                <Text size="sm" c="dimmed" fw={500}>
                  {t("vehicleFinancing:list.columns.progress")}
                </Text>
                <Text size="sm" fw={600} c={progressColor}>
                  {percent.toFixed(0)}%{" "}
                  {t("vehicleFinancing:list.complete")}
                </Text>
              </Group>
              <AnimatedProgressBar
                value={percent}
                color={progressColor}
              />
            </Stack>
          )}

          {/* Bottom details */}
          {detailItems.length > 0 && (
            <>
              <Divider variant="dashed" />
              <Group gap="md" wrap="wrap" style={{ rowGap: 6 }}>
                {detailItems.map((item, idx) => (
                  <DetailItem key={idx} icon={item.icon}>
                    {item.text}
                  </DetailItem>
                ))}
              </Group>
            </>
          )}
        </Stack>

        {/* ── Right: Animated ring (tablet/desktop) ── */}
        {hasProgress && !isMobile && (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <AnimatedRing
              value={percent}
              size={80}
              thickness={6}
              color={progressColor}
            />
          </Box>
        )}
      </Group>

      {/* Desktop inline action buttons */}
      {!isMobile && (onEdit || onDelete) && (
        <Group
          justify="flex-end"
          gap={8}
          px="md"
          py="xs"
          mt="sm"
          style={{
            borderTop: "1px solid var(--mantine-color-gray-1)",
            backgroundColor: "var(--mantine-color-gray-0)",
            marginLeft: "calc(-1 * var(--mantine-spacing-lg))",
            marginRight: "calc(-1 * var(--mantine-spacing-lg))",
            marginBottom: "calc(-1 * var(--mantine-spacing-lg))",
            paddingLeft: "var(--mantine-spacing-lg)",
            paddingRight: "var(--mantine-spacing-lg)",
            borderRadius:
              "0 0 var(--mantine-radius-md) var(--mantine-radius-md)",
          }}
        >
          {onEdit && (
            <AppButton
              iconName="Edit"
              variant="secondary"
              size="sm"
              onClick={() => onEdit(financing)}
              disabled={isDeleting}
            >
              {t("common:buttons.edit")}
            </AppButton>
          )}
          {onDelete && (
            <AppButton
              iconName="Trash"
              variant="secondary"
              size="sm"
              color="red"
              onClick={() => onDelete(financing)}
              loading={isDeleting}
              disabled={isDeleting}
            >
              {t("common:buttons.delete")}
            </AppButton>
          )}
        </Group>
      )}
    </Paper>
  );

  // Wrap in SwipeableCard on mobile
  if (isMobile && leftSwipeActions.length > 0) {
    return (
      <SwipeableCard
        leftSwipeActions={leftSwipeActions}
        forceClose={forceClose}
        onForceCloseComplete={onForceCloseComplete}
        onSwipeStateChange={handleSwipeStateChange}
        disabled={isDeleting}
        borderRadius="var(--mantine-radius-md)"
      >
        {cardContent}
      </SwipeableCard>
    );
  }

  return cardContent;
};

const FinancingCard = React.memo(FinancingCardComponent);

// ============================================
// Main Component
// ============================================

const VehicleFinancingListComponent: React.FC<
  VehicleFinancingListProps
> = ({
  financingRecords: financingRecordsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditFinancing,
  onDeleteFinancing,
  deletingFinancingId: deletingFinancingIdProp,
  highlightedFinancingId,
  onAddFinancing,
}) => {
    const { t } = useTranslation(["vehicleFinancing", "common"]);
    const { isMobile } = useAppEnvironment();

    // ── Context vs Props resolution ──
    const financingContext = useOptionalVehicleFinancingContext();

    const financingRecords =
      financingRecordsProp ?? financingContext?.financingRecords ?? [];
    const loading =
      loadingProp ?? financingContext?.isLoading ?? false;
    const loadingMore = loadingMoreProp ?? false;
    const hasMore = hasMoreProp ?? false;
    const onLoadMore = onLoadMoreProp;
    const deletingFinancingId = deletingFinancingIdProp ?? null;

    // Swipeable card management
    const { openCard, shouldForceClose, handleForceCloseComplete } =
      useSwipeableList();

    // Entrance animation
    const { hasAnimated } = useEntranceAnimation({
      itemCount: financingRecords.length,
    });

    const handleSwipeStateChange = useCallback(
      (financingId: string, isOpen: boolean) => {
        if (isOpen) openCard(financingId);
      },
      [openCard]
    );

    // ── Loading state ──
    if (loading && financingRecords.length === 0) {
      return <LoadingSkeleton />;
    }

    // ── Empty state ──
    if (!loading && financingRecords.length === 0) {
      return (
        <Paper withBorder p="xl" radius="md">
          <EmptyState
            title={t("vehicleFinancing:emptyState.title")}
            description={t("vehicleFinancing:emptyState.description")}
            iconName="CreditCard"
            actionLabel={t("vehicleFinancing:emptyState.actionLabel")}
            onAction={onAddFinancing}
            size="md"
          />
        </Paper>
      );
    }

    return (
      <Stack gap="md">
        <motion.div
          variants={hasAnimated ? undefined : entranceListVariants}
          initial={hasAnimated ? false : "hidden"}
          animate={hasAnimated ? undefined : "visible"}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--mantine-spacing-md)",
          }}
        >
          <AnimatePresence mode="sync">
            {financingRecords.map((financing) => (
              <motion.div
                key={financing.id}
                variants={
                  hasAnimated ? undefined : entranceCardVariants
                }
                initial={hasAnimated ? false : "hidden"}
                animate={hasAnimated ? undefined : "visible"}
                exit="exit"
                layout
              >
                <FinancingCard
                  financing={financing}
                  onEdit={onEditFinancing}
                  onDelete={onDeleteFinancing}
                  highlighted={
                    financing.id === highlightedFinancingId
                  }
                  isDeleting={
                    deletingFinancingId === financing.id
                  }
                  isMobile={!!isMobile}
                  forceClose={shouldForceClose(
                    financing.id || ""
                  )}
                  onForceCloseComplete={() =>
                    handleForceCloseComplete(
                      financing.id || ""
                    )
                  }
                  onSwipeStateChange={(isOpen) =>
                    handleSwipeStateChange(
                      financing.id || "",
                      isOpen
                    )
                  }
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <LoadMoreButton
          hasMore={hasMore}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
        />
      </Stack>
    );
  };

export const VehicleFinancingList = React.memo(
  VehicleFinancingListComponent
);